// 🚀 RECIPIENT SELECTOR OPTIMISÉ - RecipientSelector.jsx - AVATARS CORRIGÉS

import React, { useState, useEffect, useRef } from 'react';
import { Search, Users, User, X, Check, ChevronDown, Loader2 } from 'lucide-react';
import { api } from '../services/api.js';

const RecipientSelector = ({ 
  selectedRecipients = [], 
  onSelectionChange,
  placeholder = "Rechercher destinataires...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipientOptions, setRecipientOptions] = useState({
    groups: [],
    individuals: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // ====================================
  // 🖼️ FONCTION AVATAR CORRIGÉE
  // ====================================
  
  const getAvatarUrl = (recipient) => {
    if (!recipient.photo_url || recipient.type !== 'individual') return null;
    
    console.log('🖼️ [RecipientSelector] getAvatarUrl input:', {
      id: recipient.id,
      photo_url: recipient.photo_url,
      avatar_url: recipient.avatar_url
    });
    
    // 🎯 MÉTHODE 1 : Si photo_url contient le chemin complet avec ID employé
    if (recipient.photo_url.includes('/')) {
      const pathParts = recipient.photo_url.split('/');
      
      if (pathParts.length >= 3) {
        const employeId = pathParts[pathParts.length - 2];
        const filename = pathParts[pathParts.length - 1];
        
        const url = `/api/messagerie/avatars/${employeId}/${filename}`;
        console.log('✅ [RecipientSelector] URL avec ID:', url);
        return url;
      }
    }
    
    // 🎯 MÉTHODE 2 : Si on a l'ID employé séparément
    if (recipient.id && recipient.photo_url) {
      const filename = recipient.photo_url.split('/').pop();
      const url = `/api/messagerie/avatars/${recipient.id}/${filename}`;
      console.log('✅ [RecipientSelector] URL avec recipient.id:', url);
      return url;
    }
    
    // 🎯 MÉTHODE 3 : Fallback - recherche dans tous les dossiers
    const filename = recipient.photo_url.split('/').pop();
    const url = `/api/messagerie/avatars/${filename}`;
    console.log('⚠️ [RecipientSelector] URL fallback:', url);
    return url;
  };

  // ====================================
  // 🎯 CHARGEMENT OPTIMISÉ DES OPTIONS
  // ====================================

  const fetchRecipientOptions = async (search = '') => {
    // 🚫 NE PAS CHERCHER SI MOINS DE 2 CARACTÈRES (sauf si vide pour groupes)
    if (search.length > 0 && search.length < 2) {
      setRecipientOptions({ groups: [], individuals: [] });
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 Recherche destinataires: "${search}"`);
      
      // 🎯 APPELS SÉPARÉS pour éviter les doublons
      const [groupsResult, employeesResult] = await Promise.all([
        // 1. Charger TOUS les groupes (pas seulement les miens)
        api.messagerie.getAllGroups(),
        
        // 2. Chercher les employés seulement si recherche
        search.length >= 2 
          ? api.messagerie.getEmployeesWithAccess(search)
          : Promise.resolve({ success: true, data: [] })
      ]);
      
      let groups = [];
      let individuals = [];
      
      // 🏗️ TRAITEMENT DES GROUPES - SIMPLIFIÉ SANS MEMBRES
      if (groupsResult.success && groupsResult.data) {
        groups = groupsResult.data.map(group => ({
          type: 'group',
          id: group.id,
          name: group.name,
          display_name: group.name,
          description: group.description,
          // Pas de member_count - retiré comme demandé
          roles: group.roles || []
        }));
        
        console.log(`✅ ${groups.length} groupes chargés (sans comptage membres)`);
      }
      
      // 👥 TRAITEMENT DES EMPLOYÉS AVEC DÉDUPLICATION
      if (employeesResult.success && employeesResult.data) {
        const rawEmployees = employeesResult.data;
        
        // 🚫 DÉDUPLICATION par ID
        const uniqueEmployees = rawEmployees.reduce((acc, emp) => {
          if (!acc.find(existing => existing.id === emp.id)) {
            acc.push(emp);
          }
          return acc;
        }, []);
        
        individuals = uniqueEmployees.map(emp => ({
          type: 'individual',
          id: emp.id,
          name: `${emp.prenom || ''} ${emp.nom || ''}`.trim(),
          display_name: `${emp.prenom || ''} ${emp.nom || ''}`.trim(),
          email: emp.email,
          // 🔧 CORRECTION AVATAR : Garder le chemin original au lieu de le modifier
          photo_url: emp.photo_url,
          avatar_url: emp.avatar_url,
          status: emp.is_online ? 'online' : 'offline',
          // Données employé complètes
          prenom: emp.prenom,
          nom: emp.nom,
          fonction: emp.fonction
        }));
        
        console.log(`✅ ${individuals.length} employés uniques chargés (${rawEmployees.length} avant déduplication)`);
      }
      
      setRecipientOptions({ groups, individuals });
      setHasSearched(true);
      
    } catch (err) {
      console.error('❌ Erreur chargement destinataires:', err);
      setError('Impossible de charger les destinataires');
      setRecipientOptions({ groups: [], individuals: [] });
    } finally {
      setLoading(false);
    }
  };

  // ====================================
  // 🎯 GESTION DES SÉLECTIONS
  // ====================================

  const isSelected = (recipient) => {
    return selectedRecipients.some(selected => 
      selected.type === recipient.type && selected.id === recipient.id
    );
  };

  const toggleSelection = (recipient) => {
    const isCurrentlySelected = isSelected(recipient);
    
    let newSelection;
    if (isCurrentlySelected) {
      // Retirer de la sélection
      newSelection = selectedRecipients.filter(selected => 
        !(selected.type === recipient.type && selected.id === recipient.id)
      );
    } else {
      // Ajouter à la sélection
      newSelection = [...selectedRecipients, {
        type: recipient.type,
        id: recipient.id,
        name: recipient.display_name || recipient.name,
        display_name: recipient.display_name || recipient.name,
        // Infos supplémentaires pour l'affichage
        member_count: recipient.member_count,
        email: recipient.email,
        status: recipient.status,
        avatar_url: recipient.avatar_url,
        photo_url: recipient.photo_url
      }];
    }
    
    console.log(`${isCurrentlySelected ? '➖' : '➕'} ${recipient.type}: ${recipient.display_name}`);
    onSelectionChange(newSelection);
  };

  const removeRecipient = (recipient) => {
    const newSelection = selectedRecipients.filter(selected => 
      !(selected.type === recipient.type && selected.id === recipient.id)
    );
    onSelectionChange(newSelection);
  };

  const clearAll = () => {
    onSelectionChange([]);
  };

  // ====================================
  // 🎯 EFFETS OPTIMISÉS
  // ====================================

  // Chargement initial des groupes seulement
  useEffect(() => {
    fetchRecipientOptions(''); // Charge les groupes au démarrage
  }, []);

  // Recherche avec debounce
  useEffect(() => {
    if (!isOpen) return;
    
    const timeoutId = setTimeout(() => {
      fetchRecipientOptions(searchQuery);
    }, 300); // Débounce de 300ms
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, isOpen]);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ====================================
  // 🎨 COMPOSANTS D'AFFICHAGE
  // ====================================

  const RecipientBadge = ({ recipient, onRemove }) => (
    <div className="inline-flex items-center gap-1 bg-[#319190] text-white px-2 py-1 rounded-md text-sm">
      {recipient.type === 'group' ? (
        <Users size={12} />
      ) : (
        <User size={12} />
      )}
      <span className="max-w-32 truncate">{recipient.display_name || recipient.name}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(recipient);
        }}
        className="hover:bg-white/20 rounded-sm p-0.5"
      >
        <X size={12} />
      </button>
    </div>
  );

  const RecipientOption = ({ recipient }) => {
    const selected = isSelected(recipient);
    
    return (
      <div
        onClick={() => toggleSelection(recipient)}
        className={`p-3 cursor-pointer border-l-4 transition-colors ${
          selected 
            ? 'bg-[#319190]/10 border-[#319190]' 
            : 'hover:bg-gray-50 border-transparent'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            {recipient.type === 'group' ? (
              <div className="w-10 h-10 bg-[#319190] rounded-full flex items-center justify-center text-white">
                <Users size={16} />
              </div>
            ) : (
              <div className="w-10 h-10 bg-[#319190] rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                {(() => {
                  const avatarUrl = getAvatarUrl(recipient);
                  
                  if (avatarUrl) {
                    return (
                      <>
                        <img 
                          src={avatarUrl}
                          alt={recipient.display_name} 
                          className="w-full h-full object-cover"
                          onLoad={() => {
                            console.log('✅ [RecipientSelector] Avatar chargé:', avatarUrl);
                          }}
                          onError={(e) => {
                            console.error('❌ [RecipientSelector] Erreur avatar:', avatarUrl);
                            // Cacher l'image et afficher les initiales
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                          style={{ display: 'block' }}
                        />
                        <span 
                          className="w-full h-full flex items-center justify-center absolute inset-0"
                          style={{ display: 'none' }}
                        >
                          {recipient.display_name?.substring(0, 2).toUpperCase() || '??'}
                        </span>
                      </>
                    );
                  }
                  
                  // Fallback: initiales
                  return recipient.display_name?.substring(0, 2).toUpperCase() || '??';
                })()}
              </div>
            )}
            
            {/* Indicateur en ligne pour les individus */}
            {recipient.type === 'individual' && recipient.status === 'online' && (
              <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="font-medium truncate">{recipient.display_name || recipient.name}</p>
              {selected && (
                <Check size={16} className="text-[#319190] flex-shrink-0" />
              )}
            </div>
            
            {recipient.type === 'group' ? (
              <div>
                {recipient.description && (
                  <p className="text-sm text-gray-500 truncate">{recipient.description}</p>
                )}
                {/* 🎯 AFFICHAGE DES RÔLES SEULEMENT */}
                {recipient.roles && recipient.roles.length > 0 && (
                  <p className="text-xs text-blue-600 truncate mt-1">
                    {recipient.roles.slice(0, 3).join(', ')}
                    {recipient.roles.length > 3 && ` +${recipient.roles.length - 3}`}
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-500 truncate">{recipient.email}</p>
                {recipient.fonction && (
                  <p className="text-xs text-gray-400 truncate mt-1">{recipient.fonction}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ====================================
  // 🎨 RENDER PRINCIPAL
  // ====================================

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Champ de sélection */}
      <div
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
          }
        }}
        className="min-h-[44px] w-full border rounded-lg bg-white cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-[#319190] focus-within:border-[#319190]"
      >
        <div className="p-2">
          {/* Destinataires sélectionnés */}
          {selectedRecipients.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {selectedRecipients.map((recipient, index) => (
                <RecipientBadge
                  key={`${recipient.type}-${recipient.id}`}
                  recipient={recipient}
                  onRemove={removeRecipient}
                />
              ))}
            </div>
          )}
          
          {/* Champ de recherche / placeholder */}
          <div className="flex items-center gap-2">
            <Search size={16} className="text-gray-400 flex-shrink-0" />
            
            {isOpen ? (
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Tapez au moins 2 lettres pour chercher des employés..."
                className="flex-1 outline-none bg-transparent"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsOpen(false);
                  }
                }}
              />
            ) : (
              <span className={`flex-1 ${selectedRecipients.length === 0 ? 'text-gray-500' : 'text-gray-700'}`}>
                {selectedRecipients.length === 0 
                  ? placeholder
                  : `${selectedRecipients.length} destinataire${selectedRecipients.length > 1 ? 's' : ''} sélectionné${selectedRecipients.length > 1 ? 's' : ''}`
                }
              </span>
            )}
            
            <div className="flex items-center gap-1">
              {selectedRecipients.length > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAll();
                  }}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  title="Tout effacer"
                >
                  <X size={14} />
                </button>
              )}
              
              <ChevronDown 
                size={16} 
                className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Dropdown des options */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-96 overflow-hidden z-[9999]">
          {error ? (
            <div className="p-4 text-center text-red-600">
              <p>{error}</p>
              <button
                onClick={() => fetchRecipientOptions(searchQuery)}
                className="mt-2 text-sm text-[#319190] hover:underline"
              >
                Réessayer
              </button>
            </div>
          ) : loading ? (
            <div className="p-4 text-center">
              <Loader2 size={20} className="animate-spin mx-auto text-[#319190]" />
              <p className="text-sm text-gray-500 mt-2">Recherche en cours...</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto">
              {/* 👥 EMPLOYÉS EN PREMIER si recherche active */}
              {searchQuery.length >= 2 && recipientOptions.individuals.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-blue-50 border-b text-sm font-medium text-gray-700 sticky top-0 z-10">
                    🔍 Employés trouvés ({recipientOptions.individuals.length})
                  </div>
                  {recipientOptions.individuals.map(individual => (
                    <RecipientOption key={`individual-${individual.id}`} recipient={individual} />
                  ))}
                </div>
              )}
              
              {/* 🏗️ GROUPES APRÈS (ou en premier si pas de recherche) */}
              {recipientOptions.groups.length > 0 && (
                <div>
                  <div className="px-3 py-2 bg-gray-50 border-b text-sm font-medium text-gray-700 sticky top-0 z-10">
                    Groupes de diffusion ({recipientOptions.groups.length})
                  </div>
                  {recipientOptions.groups.map(group => (
                    <RecipientOption key={`group-${group.id}`} recipient={group} />
                  ))}
                </div>
              )}
              
              {/* 🎯 MESSAGES D'AIDE */}
              {!hasSearched && recipientOptions.groups.length > 0 && recipientOptions.individuals.length === 0 && (
                <div className="p-4 text-center text-gray-500 border-t">
                  <p className="text-sm">💡 Tapez au moins 2 lettres pour rechercher des employés</p>
                </div>
              )}
              
              {hasSearched && searchQuery.length >= 2 && recipientOptions.individuals.length === 0 && (
                <div className="p-4 text-center text-gray-500 border-t">
                  <p>Aucun employé trouvé pour "{searchQuery}"</p>
                </div>
              )}
              
              {/* Aucun résultat du tout */}
              {recipientOptions.groups.length === 0 && recipientOptions.individuals.length === 0 && hasSearched && (
                <div className="p-4 text-center text-gray-500">
                  <p>Aucun destinataire disponible</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecipientSelector;